import { errorHandler, successHandler } from "@/utils/functions";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

const initialState = {
  savedFilters: [],
  currentFilter: null,
  error: "",
  loading: false,
};

// حفظ فلتر جديد
export const saveFilter = createAsyncThunk(
  "filter/saveFilter",
  async (values) => {
    try {
      const { data } = await axios({
        method: "post",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json;charset=UTF-8",
        },
        url: `filter`,
        data: values,
      });
      return successHandler(data, "Filter saved successfully");
    } catch (error) {
      return errorHandler(error, true);
    }
  }
);

// الحصول على الفلاتر المحفوظة لصفحة معينة
export const loadSavedFilters = createAsyncThunk(
  "filter/loadSavedFilters",
  async (page) => {
    try {
      const { data } = await axios.get(`filter/${page}`);
      return successHandler(data);
    } catch (error) {
      return errorHandler(error);
    }
  }
);

// الحصول على فلتر محفوظ محدد
export const loadSavedFilter = createAsyncThunk(
  "filter/loadSavedFilter",
  async (id) => {
    try {
      const { data } = await axios.get(`filter/single/${id}`);
      return successHandler(data);
    } catch (error) {
      return errorHandler(error);
    }
  }
);

// تحديث فلتر محفوظ
export const updateSavedFilter = createAsyncThunk(
  "filter/updateSavedFilter",
  async ({ id, values }) => {
    try {
      const { data } = await axios({
        method: "put",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json;charset=UTF-8",
        },
        url: `filter/${id}`,
        data: values,
      });
      return successHandler(data, "Filter updated successfully");
    } catch (error) {
      return errorHandler(error, true);
    }
  }
);

// حذف فلتر محفوظ
export const deleteSavedFilter = createAsyncThunk(
  "filter/deleteSavedFilter",
  async (id) => {
    try {
      const { data } = await axios({
        method: "patch",
        url: `filter/${id}`,
      });
      return successHandler(data, "Filter deleted successfully");
    } catch (error) {
      return errorHandler(error, true);
    }
  }
);

const filterSlice = createSlice({
  name: "filter",
  initialState,
  reducers: {
    setCurrentFilter: (state, action) => {
      state.currentFilter = action.payload;
    },
    clearCurrentFilter: (state) => {
      state.currentFilter = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Save Filter
      .addCase(saveFilter.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(saveFilter.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        if (payload && (payload.message === "success" || payload.success)) {
          const data = payload.data?.data || payload.data || payload;
          state.savedFilters = [...state.savedFilters, data];
        } else {
          state.error = payload?.error || "Failed to save filter";
        }
      })
      .addCase(saveFilter.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Load Saved Filters
      .addCase(loadSavedFilters.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(loadSavedFilters.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        if (payload) {
          // جلب البيانات سواء كانت في payload.data أو payload مباشرة
          const rawData = payload.data !== undefined ? payload.data : payload;
          
          let actualData = [];
          if (Array.isArray(rawData)) {
            actualData = rawData;
          } else if (rawData && Array.isArray(rawData.data)) {
            actualData = rawData.data;
          } else if (rawData && rawData.data && Array.isArray(rawData.data)) {
            actualData = rawData.data;
          } else if (typeof rawData === 'object' && rawData !== null) {
            // البحث عن أول مصفوفة متوفرة في حالة وجود تغليف غير متوقع
            const possibleArray = Object.values(rawData).find(val => Array.isArray(val));
            if (possibleArray) actualData = possibleArray;
          }

          state.savedFilters = actualData;
          state.error = "";
        } else {
          state.savedFilters = [];
          state.error = "No payload received";
        }
      })
      .addCase(loadSavedFilters.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.savedFilters = [];
      })
      // Load Single Saved Filter
      .addCase(loadSavedFilter.fulfilled, (state, action) => {
        const payload = action.payload;
        if (payload && (payload.message === "success" || payload.success)) {
          state.currentFilter = payload.data?.data || payload.data || payload;
        }
      })
      // Update Saved Filter
      .addCase(updateSavedFilter.fulfilled, (state, action) => {
        const payload = action.payload;
        if (payload && (payload.message === "success" || payload.success)) {
          const data = payload.data?.data || payload.data || payload;
          const index = state.savedFilters.findIndex(
            (f) => f.id === data.id
          );
          if (index !== -1) {
            state.savedFilters[index] = data;
          }
        }
      })
      // Delete Saved Filter
      .addCase(deleteSavedFilter.fulfilled, (state, action) => {
        const payload = action.payload;
        if (payload && (payload.message === "success" || payload.success)) {
          const data = payload.data?.data || payload.data || payload;
          const idToDelete = data?.id || payload.id;
          if (idToDelete) {
            state.savedFilters = state.savedFilters.filter(
              (f) => f.id !== idToDelete
            );
          }
        }
      });
  },
});

export const { setCurrentFilter, clearCurrentFilter } = filterSlice.actions;
export default filterSlice.reducer;
